import React from 'react';

import { SortableTable } from '~/components/core';
import EmptyState from '~/components/core/EmptyState';
import StatusChipCore from '~/components/core/StatusChipCore';
import PencilIcon from '~/components/icons/PencilIcon';
import InlineIconButton from '~/components/InlineIconButton';

import copies from './copies';
import { findScopeConfiguration, scopeStatus } from './scope';
import type { ScopeConfiguration, ScopeRow } from './types';

interface QaScopeTableProps {
  rows: ScopeRow[];
  /**
   * The QA expert's stored rows, from one request for the whole organization rather than one per
   * row. A scope absent from this list has never been configured, which reads the same as a row
   * with both run modes off: off, no prompt, no fields.
   */
  configurations: ScopeConfiguration[];
  onConfigure: (scope: ScopeRow) => void;
}

// SortableTable keys rows by `row.id`, so each (sub-org, lob) scope needs a stable identity.
// `scope` keeps the original ScopeRow for the row callback, and the derived fields feed the
// status / prompt / field-count columns.
interface ScopeTableRow extends ScopeRow {
  id: string;
  scope: ScopeRow;
  enabled: boolean;
  hasInstructions: boolean;
  configuredFieldsCount: number;
}

const QaScopeTable: React.FC<QaScopeTableProps> = ({ rows, configurations, onConfigure }) => {
  const tableRows: ScopeTableRow[] = rows.map((row) => ({
    ...row,
    id: `${row.sub_organization_id ?? null}:${row.lob}`,
    scope: row,
    ...scopeStatus(findScopeConfiguration(configurations, row)),
  }));

  const columns = [
    { id: 'sub_organization_name', label: copies.scopeTable.subOrganizationColumn, numeric: false },
    { id: 'lob', label: copies.scopeTable.lobColumn, numeric: false },
    {
      id: 'status',
      label: copies.scopeTable.statusColumn,
      disableSort: true,
      specialCell: (row: ScopeTableRow) => (
        <StatusChipCore
          status={row.enabled ? copies.scopeTable.statusOn : copies.scopeTable.statusOff}
          color={row.enabled ? 'green' : 'grey'}
        />
      ),
    },
    {
      id: 'prompt',
      label: copies.scopeTable.promptColumn,
      disableSort: true,
      specialCell: (row: ScopeTableRow) =>
        row.hasInstructions ? copies.scopeTable.promptConfigured : copies.scopeTable.promptNotConfigured,
    },
    { id: 'configuredFieldsCount', label: copies.scopeTable.fieldsColumn, numeric: true },
    {
      id: 'actions',
      label: copies.scopeTable.actionColumn,
      disableSort: true,
      specialCell: (row: ScopeTableRow) => (
        <InlineIconButton
          icon={PencilIcon}
          tooltipTitle={copies.scopeTable.actionColumn}
          onClick={() => onConfigure(row.scope)}
        />
      ),
    },
  ];

  return (
    <SortableTable
      columns={columns}
      rows={tableRows}
      emptyStateComponent={
        <EmptyState title={copies.scopeTable.emptyTitle} subtitle={copies.scopeTable.emptySubtitle} />
      }
    />
  );
};

export default QaScopeTable;
